/**
 * @author Красильников Даниил <dkrasilnikov@gmail.com>
 * @link https://t.me/krasilneg
 * 
 * @author Колесник Данил <90.kalias.3@gmail.com>
 * @link https://t.me/kalias_90
 */

import React, { CSSProperties, PropsWithChildren, ReactNode, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import Container from '@/components/Container.js';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Caption from '@/components/Span.js';
import HintedIcon from '@/components/HintedIcon.js';

export type TabledListGroup = {
  code: string;
  name: string;
  opened: boolean;
  hidden?: boolean;
  list: Array<{
    code: string;
    name: ReactNode | string;
    hidden?: boolean;
    value: ReactNode | string;
    hint?: string;
  }>
};

type TabledListProps = {
  groups: Array<TabledListGroup>;
  forcedOpenAll: boolean,
  switchGroup: (code: string, opened: boolean) => void;
};

const AttrGroupsList = styled('div')({
  gap: '6px',
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  marginTop: '6px',
  '&::first-of-type': {
    marginTop: 0
  }
});

const AttrGroupName = styled(Container)({
  fontWeight: 600,
  display: 'flex',
  justifyContent: 'space-between',
  cursor: 'pointer',
  height: '32px',
  backgroundColor: '#FAFAFA',
  borderRadius: '4px',
  padding: '4px 4px 4px 10px',
  alignItems: 'center',
  color: '#091E42',
});

const AttrRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '6px 10px 6px 10px',
  alignItems: 'center',
  minHeight: '28px',
  borderBottom: '1px solid #fafafa',
  '&:last-child': {
    borderBottom: 'none',
  }
});

const AttrName = styled(Caption)({
  flexGrow: 0,
});

const AttrValue = styled(Caption)({
  minWidth: '44px',
  textAlign: 'right',
  flexGrow: 1,
  marginLeft: '12px'
});

interface AttrGroupProps {
  code: string;
  opened: boolean;
  switchGroup: (code: string, opened: boolean) => void;
}

const ICON_STYLES: CSSProperties = {
  fontSize: '24px',
}

const AttrGroup = ({ code, children, opened, switchGroup }: AttrGroupProps & PropsWithChildren) => {
  const onGroupClick = useCallback(() => { switchGroup(code, !opened); }, [code, opened, switchGroup]);
  return  <AttrGroupName onClick={onGroupClick} className="attr-group-title">
    {children}
    {opened
      ? <ExpandMoreIcon style={ICON_STYLES} viewBox='0 0 24 24'/>
      : <ChevronRightIcon style={ICON_STYLES} viewBox='0 0 24 24'/>
    }
  </AttrGroupName>
}

const ViewerWindowContentList = ({ groups, switchGroup, forcedOpenAll }: TabledListProps) => {
  return <AttrGroupsList>
    {groups.map(({ code, name, list, opened, hidden = false }) => {
      return !hidden && !!list.length && (
        <div key={code}>
          <AttrGroup code={code} opened={opened} switchGroup={switchGroup}>{name}</AttrGroup>
          {(opened || forcedOpenAll) && list.filter(({ hidden }) => !hidden).map(({ code, name, value, hint }) => {
            return (
              <AttrRow key={code}>
                <AttrName>{name}</AttrName>
                {hint && <HintedIcon>{hint}</HintedIcon>}
                <AttrValue>{value}</AttrValue>
              </AttrRow>
            );
          })}
        </div>
      );
    })}
  </AttrGroupsList>;
};

export default ViewerWindowContentList;